export type DateRangePreset = "today" | "yesterday" | "last7" | "month" | "lastMonth" | "year" | "all" | "custom";

/** Presets offered as chips, in display order ("custom" is rendered separately with its date inputs). */
export const DATE_PRESETS: Exclude<DateRangePreset, "custom">[] = [
	"today",
	"yesterday",
	"last7",
	"month",
	"lastMonth",
	"year",
	"all",
];

/** Period a report opens with when the URL names none. */
export const DEFAULT_PRESET: DateRangePreset = "month";

/** Filters a report can honor. Each report lists the ones its endpoint applies; only those are shown. */
export type ReportFilterField = "period" | "branch" | "pos" | "employee" | "category" | "paymentMethod";

export interface ReportFiltersState {
	dateRangePreset: DateRangePreset;
	customDateFrom: string;
	customDateTo: string;
	branchId: string;
	posId: string;
	employeeId: string;
	categoryId: string;
	paymentMethod: string;
}

export interface ReportFiltersParams {
	startDate?: string;
	endDate?: string;
	branchId?: string;
	posId?: string;
	createdById?: string;
	categoryId?: string;
	paymentMethod?: string;
	page?: number;
	limit?: number;
	groupBy?: "day" | "week" | "month";
}

/** What the report viewer hands each report body. */
export interface ReportBodyProps {
	/** API params holding only the filters this report honors. */
	params: ReportFiltersParams;
	filters: ReportFiltersState;
	/** Base file name for exports, e.g. "Sales invoices 2026-10-01 to 2026-10-18". */
	exportName: string;
}

export const defaultReportFiltersState: ReportFiltersState = {
	dateRangePreset: DEFAULT_PRESET,
	customDateFrom: "",
	customDateTo: "",
	branchId: "",
	posId: "",
	employeeId: "",
	categoryId: "",
	paymentMethod: "",
};

type UrlField = Exclude<keyof ReportFiltersState, "dateRangePreset">;

/** Query-string names for each filter, so a report link carries its filters (shareable, survives reload). */
const URL_KEYS: Record<UrlField, string> = {
	customDateFrom: "from",
	customDateTo: "to",
	branchId: "branch",
	posId: "pos",
	employeeId: "employee",
	categoryId: "category",
	paymentMethod: "pm",
};

const PRESET_SET = new Set<string>([...DATE_PRESETS, "custom"]);

export function filtersFromSearchParams(search: URLSearchParams): ReportFiltersState {
	const period = search.get("period") ?? "";
	const state: ReportFiltersState = {
		...defaultReportFiltersState,
		dateRangePreset: PRESET_SET.has(period) ? (period as DateRangePreset) : DEFAULT_PRESET,
	};
	for (const [field, key] of Object.entries(URL_KEYS) as [UrlField, string][]) {
		state[field] = search.get(key) ?? "";
	}
	return state;
}

/** Writes the filters into a copy of `search` (other params are kept); defaults are left out to keep links short. */
export function filtersToSearchParams(state: ReportFiltersState, search = new URLSearchParams()): URLSearchParams {
	const next = new URLSearchParams(search);
	if (state.dateRangePreset === DEFAULT_PRESET) next.delete("period");
	else next.set("period", state.dateRangePreset);
	for (const [field, key] of Object.entries(URL_KEYS) as [UrlField, string][]) {
		const isCustomDate = field === "customDateFrom" || field === "customDateTo";
		const keep = state[field] && (!isCustomDate || state.dateRangePreset === "custom");
		if (keep) next.set(key, state[field]);
		else next.delete(key);
	}
	return next;
}

/** Keeps only the filters a report honors, so a filter it ignores is never shown as applied. */
export function scopeFilters(state: ReportFiltersState, fields: ReportFilterField[]): ReportFiltersState {
	const has = (f: ReportFilterField) => fields.includes(f);
	return {
		dateRangePreset: has("period") ? state.dateRangePreset : "all",
		customDateFrom: has("period") ? state.customDateFrom : "",
		customDateTo: has("period") ? state.customDateTo : "",
		branchId: has("branch") ? state.branchId : "",
		posId: has("pos") ? state.posId : "",
		employeeId: has("employee") ? state.employeeId : "",
		categoryId: has("category") ? state.categoryId : "",
		paymentMethod: has("paymentMethod") ? state.paymentMethod : "",
	};
}

export function reportFiltersToParams(state: ReportFiltersState): ReportFiltersParams {
	const params: ReportFiltersParams = {};
	if (state.branchId) params.branchId = state.branchId;
	if (state.posId) params.posId = state.posId;
	if (state.employeeId) params.createdById = state.employeeId;
	if (state.categoryId) params.categoryId = state.categoryId;
	if (state.paymentMethod) params.paymentMethod = state.paymentMethod;

	const { startDate, endDate } = getDateRangeFromPreset(state);
	if (startDate) params.startDate = startDate;
	if (endDate) params.endDate = endDate;
	return params;
}

/** Number of active filters besides the period (shown as a count on the filters button). */
export function countExtraFilters(state: ReportFiltersState, fields: ReportFilterField[]): number {
	const scoped = scopeFilters(state, fields);
	return [scoped.branchId, scoped.posId, scoped.employeeId, scoped.categoryId, scoped.paymentMethod].filter(Boolean)
		.length;
}

const pad = (n: number) => String(n).padStart(2, "0");

/** YYYY-MM-DD in local time. (toISOString() is UTC: in Riyadh, local midnight is still the previous day.) */
export function toYmd(date: Date): string {
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function parseYmd(value: string | undefined): Date | null {
	const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value ?? "");
	return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}

function addDays(date: Date, days: number): Date {
	return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

function daysInMonth(year: number, monthIndex: number): number {
	return new Date(year, monthIndex + 1, 0).getDate();
}

type PeriodState = Pick<ReportFiltersState, "dateRangePreset" | "customDateFrom" | "customDateTo">;

/** First and last day (inclusive, local) of the selected period; empty for "all" or an incomplete custom range. */
export function getDateRangeFromPreset(
	state: PeriodState,
	now: Date = new Date(),
): { startDate?: string; endDate?: string } {
	const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
	let start: Date;
	let end: Date = today;
	switch (state.dateRangePreset) {
		case "custom": {
			const from = parseYmd(state.customDateFrom);
			const to = parseYmd(state.customDateTo);
			if (!from || !to) return {};
			// A reversed range is read the way it was meant.
			const [first, last] = from <= to ? [from, to] : [to, from];
			return { startDate: toYmd(first), endDate: toYmd(last) };
		}
		case "today":
			start = today;
			break;
		case "yesterday":
			start = addDays(today, -1);
			end = start;
			break;
		case "last7":
			start = addDays(today, -6);
			break;
		case "month":
			start = new Date(today.getFullYear(), today.getMonth(), 1);
			break;
		case "lastMonth":
			start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
			end = new Date(today.getFullYear(), today.getMonth(), 0);
			break;
		case "year":
			start = new Date(today.getFullYear(), 0, 1);
			break;
		default:
			return {};
	}
	return { startDate: toYmd(start), endDate: toYmd(end) };
}

/**
 * The period just before the selected one, for "vs previous period" deltas. Month- and year-to-date compare
 * like for like (1–18 Oct vs 1–18 Sep); other ranges compare with the same number of days just before.
 * Undefined for "all" (nothing comes before it) and an incomplete custom range.
 */
export function getPreviousRange(
	state: PeriodState,
	now: Date = new Date(),
): { startDate: string; endDate: string } | undefined {
	const range = getDateRangeFromPreset(state, now);
	const start = parseYmd(range.startDate);
	const end = parseYmd(range.endDate);
	if (!start || !end) return undefined;
	if (state.dateRangePreset === "month") {
		const back = (d: Date) =>
			new Date(
				d.getFullYear(),
				d.getMonth() - 1,
				Math.min(d.getDate(), daysInMonth(d.getFullYear(), d.getMonth() - 1)),
			);
		return { startDate: toYmd(back(start)), endDate: toYmd(back(end)) };
	}
	if (state.dateRangePreset === "year") {
		const back = (d: Date) =>
			new Date(
				d.getFullYear() - 1,
				d.getMonth(),
				Math.min(d.getDate(), daysInMonth(d.getFullYear() - 1, d.getMonth())),
			);
		return { startDate: toYmd(back(start)), endDate: toYmd(back(end)) };
	}
	if (state.dateRangePreset === "lastMonth") {
		return {
			startDate: toYmd(new Date(start.getFullYear(), start.getMonth() - 1, 1)),
			endDate: toYmd(new Date(start.getFullYear(), start.getMonth(), 0)),
		};
	}
	const days = Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1;
	return { startDate: toYmd(addDays(start, -days)), endDate: toYmd(addDays(start, -1)) };
}
